import { createContext, useContext, ReactNode, useEffect } from 'react';
import { WebSocketClient } from 'wss/WebSocketClient';
import { useDispatch } from 'react-redux';
import { receiveEcsCallMessage, receiveEcsCallIdMessage, receiveEcsOfferMessage, receiveEcsRejectMessage, getVideoCallEventsRequest } from 'actions/ecsActions';
import { omit } from 'lodash';
import { VideoCall } from 'shared/models';
import { logoutRequest } from 'actions/sessionActions';
import { websocketConnected, websocketConnecting } from 'actions/websocketActions';

const WebSocketContext = createContext<WebSocketClient>(undefined as any);

export type Props = {
    client: WebSocketClient;
    children?: ReactNode;
};

export const WebSocketProvider = ({ client, children }: Props) => {
    const dispatch = useDispatch();

    useEffect(() => {
        client.on('reauth', () => {
            console.warn('Attempt reauthentication');
            dispatch(logoutRequest());
        });
        
        client.on('connecting', () => {
            dispatch(websocketConnecting({ started: Date.now() }));
        });

        client.on('connected', () => {
            dispatch(websocketConnected(true, { when: Date.now() }));
        });

        client.on('disconnected', () => {
            dispatch(websocketConnected(false, { when: Date.now() }));
        });

        client.on('message', msg => {
            const { id, scope, type } = msg;
            console.debug(`Received ${scope}/${type} ${id}`);
            switch (scope) {
                case 'ECS':
                    switch (type) {
                        case 'CALL':
                            const videoCall = omit(msg as any, 'scope', 'type') as VideoCall;
                            dispatch(receiveEcsCallMessage(videoCall));
                            dispatch(getVideoCallEventsRequest(videoCall.callId));
                            break;

                        case 'CLID':
                            dispatch(receiveEcsCallIdMessage(omit(msg as any, 'scope', 'type', 'id') as VideoCall));
                            break;

                        case 'OFFR':
                            dispatch(receiveEcsOfferMessage((msg as any).callId));
                            break;

                        case 'RJCT':
                            dispatch(receiveEcsRejectMessage((msg as any).callId));
                            break;
    
                        case 'ICEE':
                        case 'OVRD':
                            break;
                    }
                    break;
            }
        });
    }, [ dispatch, client ]);

    return (
        <WebSocketContext.Provider value={ client }>
            { children }
        </WebSocketContext.Provider>
    );
};

export const useWebSocket = () => {
    return useContext(WebSocketContext);
};
